import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { homeService } from '../services/homeService';
import { CarouselItem } from '../data/carouselData';
import { HomepageHeroImage } from '../data/homepageImages';
import {
    ImagePlusIcon,
    SaveIcon,
    LayoutGridIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon
} from './icons';

const CarouselManagementTab: React.FC = () => {
    const { t } = useTranslation();
    const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
    const [heroImages, setHeroImages] = useState<HomepageHeroImage[]>([]);
    const [heroConfig, setHeroConfig] = useState({ firstImageId: '', secondImageId: '' });
    const [editingItem, setEditingItem] = useState<Partial<CarouselItem> | null>(null);
    const [isAddingItem, setIsAddingItem] = useState(false);

    useEffect(() => {
        loadHomeData();
    }, []);

    const loadHomeData = async () => {
        const carousel = await homeService.getCarouselItems();
        const images = await homeService.getHeroImages();
        const config = await homeService.getHeroConfig();
        setCarouselItems([...carousel]);
        setHeroImages([...images]);
        setHeroConfig({ ...config });
    };

    const handleSaveHeroConfig = async () => {
        await homeService.updateHeroConfig(heroConfig);
        alert('Hero configuration saved!');
    };

    const handleAddCarouselItem = () => {
        const newItem: Omit<CarouselItem, 'id'> = {
            titleKey: '',
            description: '',
            imageUrl: '',
            link: '',
            category: 'General',
            buttonText: 'Shop Now'
        };
        setEditingItem(newItem);
        setIsAddingItem(true);
    };

    const handleEditCarouselItem = (item: CarouselItem) => {
        setEditingItem({ ...item });
        setIsAddingItem(false);
    };

    const handleDeleteCarouselItem = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            await homeService.deleteCarouselItem(id);
            await loadHomeData();
        }
    };

    const handleSaveCarouselItem = async () => {
        if (editingItem) {
            if (isAddingItem) {
                await homeService.addCarouselItem(editingItem as Omit<CarouselItem, 'id'>);
            } else if (editingItem.id) {
                await homeService.updateCarouselItem(editingItem as CarouselItem);
            }
            setEditingItem(null);
            await loadHomeData();
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
        >
            {/* Hero Images Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <ImagePlusIcon className="w-6 h-6 text-indigo-500" />
                    {t('adminDashboard_heroTitle') || 'Homepage Hero Images (Compare Section)'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-500">{t('adminDashboard_heroFirst') || 'First Hero Image'}</label>
                        <select
                            title={t('adminDashboard_heroFirst') || 'First Hero Image'}
                            value={heroConfig.firstImageId}
                            onChange={(e) => setHeroConfig({ ...heroConfig, firstImageId: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        >
                            {heroImages.map(img => (
                                <option key={img.id} value={img.id}>{img.name}</option>
                            ))}
                        </select>
                        {heroConfig.firstImageId && (
                            <img
                                src={heroImages.find(i => i.id === heroConfig.firstImageId)?.url}
                                alt="Preview"
                                className="h-32 w-full object-cover rounded-lg mt-2 border border-gray-100 dark:border-gray-700"
                            />
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-500">{t('adminDashboard_heroSecond') || 'Second Hero Image'}</label>
                        <select
                            title={t('adminDashboard_heroSecond') || 'Second Hero Image'}
                            value={heroConfig.secondImageId}
                            onChange={(e) => setHeroConfig({ ...heroConfig, secondImageId: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        >
                            {heroImages.map(img => (
                                <option key={img.id} value={img.id}>{img.name}</option>
                            ))}
                        </select>
                        {heroConfig.secondImageId && (
                            <img
                                src={heroImages.find(i => i.id === heroConfig.secondImageId)?.url}
                                alt="Preview"
                                className="h-32 w-full object-cover rounded-lg mt-2 border border-gray-100 dark:border-gray-700"
                            />
                        )}
                    </div>
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSaveHeroConfig}
                        className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium shadow-md shadow-indigo-200 dark:shadow-none"
                    >
                        <SaveIcon className="w-5 h-5" />
                        {t('adminDashboard_heroSave') || 'Save Hero Layout'}
                    </button>
                </div>
            </div>

            {/* Carousel Items Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                        <LayoutGridIcon className="w-6 h-6 text-indigo-500" />
                        {t('adminDashboard_carouselTitle') || 'Main Carousel Items'}
                    </h3>
                    <button
                        onClick={handleAddCarouselItem}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium whitespace-nowrap"
                    >
                        <PlusIcon className="w-5 h-5" />
                        {t('adminDashboard_carouselAddItem') || 'Add Item'}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {carouselItems.map(item => (
                        <div key={item.id} className="group relative bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-600 hover:shadow-md transition-all">
                            <img src={item.imageUrl} alt={t(item.titleKey) || item.titleKey} className="w-full h-40 object-cover rounded-lg mb-4" />
                            <h4 className="font-bold text-gray-900 dark:text-white mb-1">{t(item.titleKey) || item.titleKey}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{item.description}</p>
                            <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-100 dark:border-gray-600">
                                <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">{item.buttonText}</span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEditCarouselItem(item)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                                        title="Edit"
                                    >
                                        <PencilIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteCarouselItem(item.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                                        title="Delete"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit Modal / Form overlay */}
            {editingItem && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-2xl w-full shadow-2xl border border-gray-100 dark:border-gray-700 overflow-y-auto max-h-[90vh]"
                    >
                        <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                            {isAddingItem 
                                ? (t('adminDashboard_carouselAddItem') || 'Add Carousel Item')
                                : (t('adminDashboard_carouselEditItem') || 'Edit Carousel Item')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
                                    <input
                                        type="text"
                                        title="Title"
                                        value={editingItem.titleKey || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, titleKey: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Subtitle</label>
                                    <textarea
                                        title="Description"
                                        value={editingItem.description || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 h-24"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Button Text</label>
                                    <input
                                        type="text"
                                        title="Button Text"
                                        value={editingItem.buttonText || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, buttonText: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Image URL</label>
                                    <input
                                        type="text"
                                        title="Image URL"
                                        value={editingItem.imageUrl || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, imageUrl: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="https://..."
                                    />
                                    {editingItem.imageUrl && (
                                        <img src={editingItem.imageUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg mt-2 border border-gray-100" />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Link URL</label>
                                    <input
                                        type="text"
                                        title="Link URL"
                                        value={editingItem.link || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, link: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="/shop/..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-8 flex justify-end gap-3">
                            <button
                                onClick={() => setEditingItem(null)}
                                className="px-6 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveCarouselItem}
                                className="px-8 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-200 dark:shadow-none"
                            >
                                {isAddingItem 
                                    ? (t('adminDashboard_carouselCreateItem') || 'Create Item')
                                    : (t('adminDashboard_carouselSaveChanges') || 'Save Changes')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </motion.div>
    );
};

export default CarouselManagementTab;
